/**
 * @fileoverview Utility for base64url encoding.
 *
 * @description
 * ELI5: `base64url` is a way to turn binary data (like random bytes) into a safe
 * string that can be used in URLs without breaking them. It's like normal Base64
 * but replaces `+` and `/` with `-` and `_`, and removes the `=` padding at the end.
 */

/**
 * Encodes an ArrayBuffer to a base64url string.
 * Uses Buffer in Node.js environment for performance and correctness.
 * Falls back to a browser-safe implementation if Buffer is not available.
 *
 * @param buf The ArrayBuffer to encode.
 * @returns The base64url encoded string.
 */
export function base64url(buf: ArrayBuffer): string {
  const unknownBuf = buf as unknown;
  if (
    !(unknownBuf instanceof ArrayBuffer) &&
    !(
      typeof SharedArrayBuffer !== 'undefined' &&
      unknownBuf instanceof SharedArrayBuffer
    )
  ) {
    throw new TypeError('Expected ArrayBuffer or SharedArrayBuffer');
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(buf).toString('base64url');
  }

  const bytes = new Uint8Array(buf);
  const decoder = new TextDecoder('latin1');
  const binary = decoder.decode(bytes);

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
